import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import CarouselDetails from '../Componentes/CarouselDetails';

import {
  FaCar, FaPaw, FaSwimmer, FaDumbbell, FaWifi, FaHotTub,
  FaBath, FaLightbulb, FaWater, FaRoad, FaPhone, FaSatellite,
  FaToilet, FaGasPump, FaRulerCombined, FaUserShield, FaBuilding, FaBed, FaTree, FaUmbrellaBeach
} from 'react-icons/fa';
import { MdBalcony, MdWbSunny } from 'react-icons/md';
import { GiBarbecue, GiStreetLight, GiWashingMachine, GiElevator } from 'react-icons/gi';

import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';

import '../Css/PropertyDetails.css';

const PropertyDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const navigate = useNavigate();

  const fetchCoordinates = async (ubicacion, ciudad) => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    let partes = [ubicacion, ciudad, "Argentina"];
    let direccion = [...new Set(partes.filter(Boolean))].join(', ');
    if (!direccion.toLowerCase().includes('argentina')) direccion += ', Argentina';

    try {
      const response = await axios.get("https://maps.googleapis.com/maps/api/geocode/json", {
        params: { address: direccion, region: 'AR', key: apiKey },
      });
      if (response.data.status === 'OK' && response.data.results.length > 0) {
        const { lat, lng } = response.data.results[0].geometry.location;
        return { lat, lng };
      }
      return null;
    } catch (error) {
      console.error('Error al obtener coordenadas:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        const response = await axios.get(`https://backend-admin-nine.vercel.app/api/propiedades/${id}`);
        const propertyData = response.data;
        setProperty(propertyData);

        if (propertyData.lat && propertyData.lng) {
          setLocation({ lat: propertyData.lat, lng: propertyData.lng });
          setLoading(false);
          return;
        }
        const coords = await fetchCoordinates(propertyData.ubicacion, propertyData.ciudad?.name || '');
        if (coords) setLocation({ lat: coords.lat, lng: coords.lng });
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar los detalles de la propiedad:', error);
        setLoading(false);
      }
    };
    fetchPropertyDetails();
  }, [id]);

  if (loading) {
    return (
      <div>
        <Skeleton variant="rectangular" width="100%" height={400} />
        <Skeleton variant="text" width="60%" height={40} style={{ margin: '20px 0' }} />
        <Skeleton variant="text" width="80%" height={30} />
      </div>
    );
  }

  if (!property) return <div>Propiedad no encontrada</div>;

  const containerStyle = {
    width: '80%',
    height: '400px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '20px auto',
  };

  const c = property.caracteristicas || {};

  return (
    <div className="property-details">
      <CarouselDetails images={property.fotos} />

      <div className="property-info">
        <p className='ubicacion'><strong>Ubicación: </strong> {property.ubicacion || 'Dirección no disponible'}</p>
        {property.barrio && <p><strong>Barrio:</strong> {property.barrio}</p>}
        <p><strong>Valor: </strong> ${property.precio} {property.moneda}</p>
        <p><strong>Descripción: </strong> {property.descripcion}</p>
        <p><strong>Ciudad:</strong> {property.ciudad?.name || 'Ciudad no disponible'}</p>

        <div className="extra-features-grid">
          {/* Superficies y conteos */}
          {c.m2Cubiertos > 0 && <div className="extra-feature"><FaRulerCombined /><span>M² Cubiertos: {c.m2Cubiertos}</span></div>}
          {c.m2Semicubiertos > 0 && <div className="extra-feature"><FaRulerCombined /><span>M² Semicubiertos: {c.m2Semicubiertos}</span></div>}
          {c.m2Descubiertos > 0 && <div className="extra-feature"><FaRulerCombined /><span>M² Descubiertos: {c.m2Descubiertos}</span></div>}
          {c.m2Totales > 0 && <div className="extra-feature"><FaRulerCombined /><span>M² Totales: {c.m2Totales}</span></div>}

          {c.ambientes > 0 && <div className="extra-feature"><FaBuilding /><span>Ambientes: {c.ambientes}</span></div>}
          {c.banos > 0 && <div className="extra-feature"><FaBath /><span>Baños: {c.banos}</span></div>}
          {c.dormitorios > 0 && <div className="extra-feature"><FaBed /><span>Dormitorios: {c.dormitorios}</span></div>}

          {/* Booleans */}
          {c.cochera && <div className="extra-feature"><FaCar /><span>Cochera</span></div>}
          {c.mascotas && <div className="extra-feature"><FaPaw /><span>Acepta Mascotas</span></div>}
          {c.pileta && <div className="extra-feature"><FaSwimmer /><span>Pileta</span></div>}
          {c.gimnasio && <div className="extra-feature"><FaDumbbell /><span>Gimnasio</span></div>}
          {c.laundry && <div className="extra-feature"><GiWashingMachine /><span>Laundry</span></div>}
          {c.ascensor && <div className="extra-feature"><GiElevator /><span>Ascensor</span></div>}

          {c.agua && <div className="extra-feature"><FaWater /><span>Agua</span></div>}
          {c.cloaca && <div className="extra-feature"><FaToilet /><span>Cloaca</span></div>}
          {c.gas && <div className="extra-feature"><FaGasPump /><span>Gas</span></div>}
          {c.internet && <div className="extra-feature"><FaWifi /><span>Internet</span></div>}
          {c.electricidad && <div className="extra-feature"><FaLightbulb /><span>Electricidad</span></div>}
          {c.pavimento && <div className="extra-feature"><FaRoad /><span>Pavimento</span></div>}
          {c.telefono && <div className="extra-feature"><FaPhone /><span>Teléfono</span></div>}
          {c.cable && <div className="extra-feature"><FaSatellite /><span>Cable</span></div>}
          {c.solarium && <div className="extra-feature"><FaHotTub /><span>Solarium</span></div>}
          {c.sum && <div className="extra-feature"><MdWbSunny /><span>SUM</span></div>}
          {c.luminoso && <div className="extra-feature"><MdWbSunny /><span>Luminoso</span></div>}
          {c.seguridad && <div className="extra-feature"><FaUserShield /><span>Seguridad 24hs</span></div>}
          {c.parrilla && <div className="extra-feature"><GiBarbecue /><span>Parrilla</span></div>}
          {c.alumbrado && <div className="extra-feature"><GiStreetLight /><span>Alumbrado</span></div>}
          {c.patio && <div className="extra-feature"><FaTree /><span>Patio</span></div>}
          {c.terraza && <div className="extra-feature"><FaUmbrellaBeach /><span>Terraza</span></div>}
          {c.balcon && <div className="extra-feature"><MdBalcony /><span>Balcón</span></div>}
          {c.galeria && <div className="extra-feature"><MdBalcony /><span>Galería</span></div>}
          {c.balconTerraza && <div className="extra-feature"><MdBalcony /><span>Balcón terraza</span></div>}
          {c.jardin && <div className="extra-feature"><FaTree /><span>Jardín</span></div>}
        </div>

        <Button
          variant="contained"
          color="success"
          style={{ marginLeft: '10px', padding: '4px 16px' }}
          startIcon={<WhatsAppIcon />}
          onClick={() => {
            const message = encodeURIComponent(
              `Hola, acabo de ver esta propiedad y quisiera más información:\n\n` +
              `Ubicación: ${property.ubicacion}\n` +
              (property.barrio ? `Barrio: ${property.barrio}\n` : ``) +
              `Valor: ${property.precio} ${property.moneda}\n` +
              `Estado: ${property.estado}\n` +
              `¡Gracias!`
            );
            const whatsappNumber = "+5491133061010";
            window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
          }}
        >
          Pedir Información
        </Button>

        <Button
          variant="contained"
          color="primary"
          style={{ marginLeft: '10px', padding: '4px 16px' }}
          startIcon={<EmailIcon />}
          onClick={() => navigate('/contacto', { state: { property } })}
        >
          Quiero que me contacten
        </Button>

        {location ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={location}
            zoom={15}
          >
            <Marker position={location} />
          </GoogleMap>
        ) : (
          <p>Ubicación no disponible para mostrar en el mapa.</p>
        )}
      </div>
    </div>
  );
};

export default PropertyDetails;
